import { useTranslation } from 'react-i18next'

import { Box, Text, VStack } from '@enechain/ecloud-designsystem'

import { type AreaSideLimit, checkBidLimit } from '~/models/bidLimits'

type Props = {
  /**
   * 解決済みの (エリア × 売買) 制限。undefined = 制限未設定 (= エリア・売買が
   * まだ確定していない、または設定に該当なし)。
   */
  limit: AreaSideLimit | undefined
  /** 現在の入力価格 (円/kWh)。未入力は undefined。 */
  price: number | undefined
  /** 現在の入力数量 (1 コマの MW = 入力欄と同じ単位)。未入力は undefined。 */
  volumeMw: number | undefined
  /** 上限数量(1札) の超過を出すか (省略時は出す)。 自動起票の札は出さない (AC-BSP-103)。 */
  checksVolumeMax?: boolean
  /**
   * 入札制限をまだ取得できていない理由 (取得中 / 取得失敗)。 指定時は `limit` を見ずに
   * その旨を出す (= 取得できていない間の空 lookup を「制限未設定」と断定しない)。
   */
  unavailable?: 'loading' | 'error'
}

const fmt = (v: number | undefined): string =>
  v !== undefined ? String(v) : '—'

/**
 * 札作成 / 編集モーダルの入札制限インラインヒント (design spot.html (B) /
 * intraday.html の `paintIntradayLimitHint` 相当)。 スポット入札・時間前入札の
 * 双方が使う (= 2 画面目の利用者が出た時点で page-local から共有へ昇格した)。
 *
 * エリア + 売/買 が確定した時点で「下限 / 上限 / 上限数量」を表示し、入力価格 / 数量が
 * 範囲外なら inline error を出す。制限未設定なら「制限未設定」を表示する。 どの市場の
 * 制限を出すかは呼び出し元が解決済みの `limit` を渡して決める (= スポットは
 * SPOT_NORMAL、 時間前は INTRADAY の matrix)。
 *
 * 判定は settings「入札制限」の実設定値 (= `~/models/bidLimits` の checkBidLimit) を
 * 用いる。この表示自体は UX 事前チェック (= 本 component は setError を呼ばない)。
 * スポット入札の数量軸 (上限数量(1札)) は保存直前にも同じ判定を適用して submit を
 * ブロックする (AC-BSP-103、 呼び出し元の SpotBidCreateModal / IndividualBidModal の
 * handleSave)。 それ以外の提出ブロックは承認申請時 (AC-BSP-008) / 提出時
 * (AC-BIN-015) と backend 側で行う。
 */
export const BidLimitHint = ({
  limit,
  price,
  volumeMw,
  checksVolumeMax = true,
  unavailable,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  if (unavailable !== undefined) {
    return (
      <Text color="Text.lowEmphasis" fontSize="sm">
        {unavailable === 'loading'
          ? t('page:biddingCommon.bidLimitHint.loading')
          : t('page:biddingCommon.bidLimitHint.unavailable')}
      </Text>
    )
  }

  if (limit === undefined) {
    return (
      <Text color="Text.lowEmphasis" fontSize="sm">
        {t('page:biddingCommon.bidLimitHint.unset')}
      </Text>
    )
  }

  // 制限 QTY・入力欄とも MW (AC-BSP-053) なのでそのまま突き合わせる。
  const violations = checkBidLimit(
    price,
    checksVolumeMax ? volumeMw : undefined,
    limit,
  )
  const priceError = violations.includes('price-min')
    ? t('page:biddingCommon.bidLimitHint.priceMinError', {
        min: fmt(limit.priceMin),
      })
    : violations.includes('price-max')
      ? t('page:biddingCommon.bidLimitHint.priceMaxError', {
          max: fmt(limit.priceMax),
        })
      : undefined
  const volumeError = violations.includes('volume-max')
    ? t('page:biddingCommon.bidLimitHint.volumeMaxError', {
        max: fmt(limit.qtyMax),
      })
    : undefined

  return (
    <VStack align="stretch" gap={1}>
      <Box color="Text.midEmphasis" fontSize="sm">
        {t('page:biddingCommon.bidLimitHint.summary', {
          min: fmt(limit.priceMin),
          max: fmt(limit.priceMax),
          qty: fmt(limit.qtyMax),
        })}
      </Box>
      {priceError !== undefined && (
        <Text color="Text.negativeMid" fontSize="sm">
          {priceError}
        </Text>
      )}
      {volumeError !== undefined && (
        <Text color="Text.negativeMid" fontSize="sm">
          {volumeError}
        </Text>
      )}
    </VStack>
  )
}
